/**
 * React Hook for GEX WebSocket Connection
 *
 * Usage:
 *   const { gexData, isConnected, subscribe, unsubscribe } = useGexWebSocket(sessionToken);
 *
 *   useEffect(() => {
 *     subscribe('SPY');
 *     return () => unsubscribe('SPY');
 *   }, []);
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const WS_URL = process.env.REACT_APP_WS_URL || (
  window.location.hostname === 'localhost'
    ? 'ws://localhost:4001/ws'
    : `wss://${window.location.host}/ws`
);

export function useGexWebSocket(sessionToken) {
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [gexData, setGexData] = useState({}); // { symbol: data }
  const [vixData, setVixData] = useState(null); // VIX data from server
  const [vwapData, setVwapData] = useState({}); // { symbol: { vwap, proxyVwap, proxy, multiplier } }
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const subscribedSymbols = useRef(new Set());
  const pendingSubscriptions = useRef(new Map()); // symbol -> { startDate, endDate }
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);

  const handleMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'connected':
        console.log('[WS] Server welcome:', msg.clientId);
        break;

      case 'auth_success':
        setIsAuthenticated(true);
        // Send any pending subscriptions now that we're authenticated
        for (const [symbol, dateRange] of pendingSubscriptions.current) {
          const message = { type: 'subscribe', symbol };
          if (dateRange) {
            message.startDate = dateRange.startDate;
            message.endDate = dateRange.endDate;
          }
          wsRef.current?.send(JSON.stringify(message));
        }
        pendingSubscriptions.current.clear();
        break;

      case 'auth_failed':
        console.error('[WS] Auth failed:', msg.message);
        setError(msg.message);
        setIsAuthenticated(false);
        break;

      case 'session_kicked':
        console.warn('[WS] Session kicked:', msg.message);
        setError(msg.message);
        setIsAuthenticated(false);
        break;

      case 'subscribed':
        console.log('[WS] Subscribed to', msg.symbol);
        break;

      case 'gex':
        // Update GEX data for this symbol
        setGexData(prev => ({
          ...prev,
          [msg.symbol]: msg.data
        }));
        // Update VIX data if present
        if (msg.vix) {
          setVixData(msg.vix);
        }
        // Update VWAP data if present
        if (msg.vwap) {
          setVwapData(prev => ({
            ...prev,
            [msg.symbol]: msg.vwap
          }));
        }
        break;

      case 'error':
        console.error('[WS] Server error:', msg.message);
        setError(msg.message);
        break;

      case 'pong':
        // Heartbeat response
        break;

      default:
        console.log('[WS] Unknown message type:', msg.type);
    }
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    console.log('[WS] Connecting to', WS_URL);
    const ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      console.log('[WS] Connected');
      setIsConnected(true);
      setError(null);
      reconnectAttempts.current = 0;

      // Authenticate if we have a token
      if (sessionToken) {
        ws.send(JSON.stringify({ type: 'auth', token: sessionToken }));
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        handleMessage(msg);
      } catch (err) {
        console.error('[WS] Failed to parse message:', err);
      }
    };

    ws.onclose = (event) => {
      console.log('[WS] Disconnected:', event.code, event.reason);
      // Only update state if this is still the active connection
      // (prevents stale close events from clobbering a newer connection)
      if (wsRef.current !== ws) return;

      setIsConnected(false);
      setIsAuthenticated(false);
      wsRef.current = null;

      // Reconnect with exponential backoff
      if (!event.wasClean) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        console.log(`[WS] Reconnecting in ${delay}ms...`);
        reconnectTimeout.current = setTimeout(() => {
          reconnectAttempts.current++;
          connect();
        }, delay);
      }
    };

    ws.onerror = (err) => {
      console.error('[WS] Error:', err);
      setError('WebSocket connection error');
    };

    wsRef.current = ws;
  }, [sessionToken, handleMessage]);

  const subscribe = useCallback((symbol, dateRange = null) => {
    const normalizedSymbol = symbol.toUpperCase();
    subscribedSymbols.current.add(normalizedSymbol);
    pendingSubscriptions.current.set(normalizedSymbol, dateRange);

    const message = { type: 'subscribe', symbol: normalizedSymbol };
    if (dateRange) {
      message.startDate = dateRange.startDate;
      message.endDate = dateRange.endDate;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN && isAuthenticated) {
      wsRef.current.send(JSON.stringify(message));
      pendingSubscriptions.current.delete(normalizedSymbol);
    }
  }, [isAuthenticated]);

  const updateDateRange = useCallback((symbol, startDate, endDate) => {
    const normalizedSymbol = symbol.toUpperCase();
    if (wsRef.current?.readyState === WebSocket.OPEN && isAuthenticated) {
      wsRef.current.send(JSON.stringify({
        type: 'update_date_range',
        symbol: normalizedSymbol,
        startDate,
        endDate
      }));
    }
  }, [isAuthenticated]);

  const requestRefresh = useCallback((symbol, interval = null) => {
    const normalizedSymbol = symbol.toUpperCase();
    if (wsRef.current?.readyState === WebSocket.OPEN && isAuthenticated) {
      console.log('[WS] Requesting refresh for', normalizedSymbol, interval ? `(interval: ${interval})` : '');
      wsRef.current.send(JSON.stringify({
        type: 'refresh',
        symbol: normalizedSymbol,
        interval: interval
      }));
    }
  }, [isAuthenticated]);

  const unsubscribe = useCallback((symbol) => {
    const normalizedSymbol = symbol.toUpperCase();
    subscribedSymbols.current.delete(normalizedSymbol);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe', symbol: normalizedSymbol }));
    }

    setGexData(prev => {
      const next = { ...prev };
      delete next[normalizedSymbol];
      return next;
    });
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
  }, []);

  // Connect on mount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // Re-authenticate when token changes
  useEffect(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && sessionToken) {
      wsRef.current.send(JSON.stringify({ type: 'auth', token: sessionToken }));
    }
  }, [sessionToken]);

  // Send pending subscriptions when authenticated
  useEffect(() => {
    if (isAuthenticated && wsRef.current?.readyState === WebSocket.OPEN) {
      for (const [symbol, dateRange] of pendingSubscriptions.current) {
        const message = { type: 'subscribe', symbol };
        if (dateRange) {
          message.startDate = dateRange.startDate;
          message.endDate = dateRange.endDate;
        }
        console.log('[WS] Sending pending subscription:', symbol);
        wsRef.current.send(JSON.stringify(message));
      }
      pendingSubscriptions.current.clear();
    }
  }, [isAuthenticated]);

  // Heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(heartbeat);
  }, [isConnected]);

  return {
    isConnected,
    isAuthenticated,
    gexData,
    vixData,
    vwapData,
    error,
    subscribe,
    unsubscribe,
    updateDateRange,
    requestRefresh,
    disconnect
  };
}

export default useGexWebSocket;
